import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ImageUploadSection from "../../components/ImageUploadSection/ImageUploadSection";
import Button from "../../components/Button/Button";
import {
  filterFormErrorObject,
  isAnyKeyValueTrue,
  mapObjectValues,
  objectHasKeys,
  validateEmail,
  validatePhoneNumber,
  validatePostalCode,
  validatePresence,
} from "../../helpers/validationHelpers";
import { Dispatch, SetStateAction, useState } from "react";
import moment from "moment";
import useFormFieldsBlurState from "../../hooks/useFormFieldsBlurState";
import { uploadAssetToCloudinary } from "../../utils/cloudinary";
import PublicOrganizationAutoComplete from "../../components/OrganizationAutoComplete/PublicOrganizationAutoComplete";
import ErrorHelperText from "../../components/ErrorHelperText/ErrorHelperText";
import PhoneNumberTextField from "../../components/PhoneNumberTextField/PhoneNumberTextField";
import { DatePicker } from "@mui/x-date-pickers";
import cities from "../../utils/cities.json";
import provinces from "../../utils/provinces.json";
const DriverProfileDetailsForm = ({
  organization,
  setOrganization,
  organizationSearchText,
  setOrganizationSearchText,
  currentDate,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  avatar,
  setAvatar,
  avatarUploadLoading,
  setAvatarUploadLoading,
  email,
  setEmail,
  phoneNumber,
  setPhoneNumber,
  dateOfBirth,
  setDateOfBirth,
  address,
  setAddress,
  city,
  setCity,
  province,
  setProvince,
  driverPostalCode,
  setDriverPostalCode,
}: {
  organization: any;
  setOrganization: Dispatch<SetStateAction<any>>;
  organizationSearchText: string;
  setOrganizationSearchText: Dispatch<SetStateAction<string>>;
  currentDate: any;
  firstName: string;
  setFirstName: Dispatch<SetStateAction<string>>;
  lastName: string;
  setLastName: Dispatch<SetStateAction<string>>;
  avatar: string;
  setAvatar: Dispatch<SetStateAction<string>>;
  avatarUploadLoading: boolean;
  setAvatarUploadLoading: Dispatch<SetStateAction<boolean>>;
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  phoneNumber: string;
  setPhoneNumber: Dispatch<SetStateAction<string>>;
  dateOfBirth: any;
  setDateOfBirth: Dispatch<SetStateAction<any>>;
  address: string;
  setAddress: Dispatch<SetStateAction<string>>;
  city: string;
  setCity: Dispatch<SetStateAction<string>>;
  province: string;
  setProvince: Dispatch<SetStateAction<string>>;
  driverPostalCode: string;
  setDriverPostalCode: Dispatch<SetStateAction<string>>;
}) => {
  const {
    blurredFormFields,
    setFormFieldToBlurred,
    setFormFieldToFocused,
    resetBlurState,
  } = useFormFieldsBlurState({
    firstName,
    lastName,
    email,
    dateOfBirth,
    phoneNumber,
    address,
    city,
    province,
    driverPostalCode,
  });

  const formErrors = {
    organization: {
      empty: !organization?.id && "Please select an organization",
    },
    firstName: {
      error:
        blurredFormFields.firstName &&
        !validatePresence(firstName) &&
        "Please enter a first name",
    },
    lastName: {
      error:
        blurredFormFields.lastName &&
        !validatePresence(lastName) &&
        "Please enter a last name",
    },
    email: {
      error: !validateEmail(email) && "Please enter a valid email",
    },
    phoneNumber: {
      error:
        blurredFormFields.phoneNumber &&
        !validatePresence(phoneNumber) &&
        !validatePhoneNumber(phoneNumber) &&
        "Please enter a valid phone number",
    },
    address: {
      error:
        blurredFormFields.address &&
        !validatePresence(address) &&
        "Please enter an address",
    },
    city: {
      error:
        blurredFormFields.city &&
        !validatePresence(city) &&
        "Please enter a city",
    },
    province: {
      error: !validatePresence(province) && "Please enter a province",
    },
    driverPostalCode: {
      empty:
        blurredFormFields.driverPostalCode &&
        !validatePresence(driverPostalCode),
      error:
        blurredFormFields.driverPostalCode &&
        !validatePostalCode(driverPostalCode) &&
        "Invalid postal code",
    },
    // drivingLicenseNumber: {
    //   empty:
    //     blurredFormFields.drivingLicenseNumber &&
    //     !validatePresence(drivingLicenseNumber) &&
    //     "Please enter a driving license number",
    //   error:
    //     blurredFormFields.drivingLicenseNumber &&
    //     !validatePresence(drivingLicenseNumber) &&
    //     !validateDrivingLicenseNumber(drivingLicenseNumber) &&
    //     "Kindly enter the driver license number without any spaces or special characters",
    // },
    dateOfBirth: {
      empty: !dateOfBirth && "Please enter your date of birth",
      tooYoung:
        dateOfBirth &&
        moment().diff(dateOfBirth, "years", true) < 18 &&
        "You must be at least 18 years of age",
      tooOld:
        dateOfBirth &&
        moment().diff(dateOfBirth, "years", true) > 120 &&
        "You must be younger than 120 years old",
    },
  };

  const parsedFormErrors = filterFormErrorObject(formErrors);

  const handleAvatarUpload = async (file: any) => {
    setAvatarUploadLoading(true);
    try {
      const data = await uploadAssetToCloudinary(file, "profile_pictures");
      setAvatar(data?.file_url);

      console.log("data", data);
    } catch (error: any) {
      console.log("error", error);
    } finally {
      setAvatarUploadLoading(false);
    }
  };

  return (
    <>
      <Grid container item spacing={2}>
        <Grid item xs={12}>
          <PublicOrganizationAutoComplete
            inputValue={organizationSearchText}
            setInputValue={setOrganizationSearchText}
            value={organization}
            setValue={setOrganization}
          />
          {mapObjectValues(
            parsedFormErrors.organization,
            (key: any, index: any) => (
              <ErrorHelperText
                key={`admin-invite-organization-errors-${index}`}
                errorText={parsedFormErrors.organization[key]}
              />
            )
          )}
        </Grid>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={6}>
          <TextField
            label={"First Name"}
            fullWidth
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
            }}
            error={Boolean(isAnyKeyValueTrue(formErrors.firstName))}
            onFocus={() => {
              setFormFieldToFocused("firstName");
            }}
            onBlur={() => {
              setFormFieldToBlurred("firstName");
            }}
          />
          {mapObjectValues(
            parsedFormErrors?.firstName,
            (key: any, index: any) => (
              <ErrorHelperText
                key={`create-new-driver-driver-profile-details-first-name-errors-${index}`}
                errorText={parsedFormErrors?.firstName[key]}
              />
            )
          )}
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={"Last Name"}
            fullWidth
            value={lastName}
            error={Boolean(isAnyKeyValueTrue(formErrors.lastName))}
            onChange={(e) => {
              setLastName(e.target.value);
            }}
            onFocus={() => {
              setFormFieldToFocused("lastName");
            }}
            onBlur={() => {
              setFormFieldToBlurred("lastName");
            }}
          />
          {mapObjectValues(
            parsedFormErrors?.lastName,
            (key: any, index: any) => (
              <ErrorHelperText
                key={`create-new-driver-driver-profile-last-name-errors-${index}`}
                errorText={parsedFormErrors?.lastName[key]}
              />
            )
          )}
        </Grid>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={6}>
          <PhoneNumberTextField
            label="Phone Number"
            placeholder="Phone Number"
            error={Boolean(isAnyKeyValueTrue(formErrors.phoneNumber))}
            value={phoneNumber}
            setValue={setPhoneNumber}
            onFocus={() => {
              setFormFieldToFocused("phoneNumber");
            }}
            onBlur={() => {
              setFormFieldToBlurred("phoneNumber");
            }}
          />
          {mapObjectValues(
            parsedFormErrors?.phoneNumber,
            (key: any, index: any) => (
              <ErrorHelperText
                key={`create-new-driver-driver-profile-phone-number-errors-${index}`}
                errorText={parsedFormErrors?.phoneNumber[key]}
              />
            )
          )}
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={"Email"}
            fullWidth
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            error={Boolean(isAnyKeyValueTrue(formErrors.email))}
            onFocus={() => {
              setFormFieldToFocused("email");
            }}
            onBlur={() => {
              setFormFieldToBlurred("email");
            }}
          />
          {mapObjectValues(parsedFormErrors?.email, (key: any, index: any) => (
            <ErrorHelperText
              key={`create-new-driver-driver-profile-email-errors-${index}`}
              errorText={parsedFormErrors?.email[key]}
            />
          ))}
        </Grid>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={12}>
          <DatePicker
            label="Date of Birth"
            value={dateOfBirth}
            onChange={(newValue: any) => {
              setDateOfBirth(newValue);
            }}
            minDate={moment.utc(currentDate).subtract(120, "years")}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                onBlur: () => {
                  setFormFieldToBlurred("dateOfBirth");
                },
                onFocus: () => {
                  setFormFieldToFocused("dateOfBirth");
                },
                error: Boolean(objectHasKeys(parsedFormErrors.dateOfBirth)),
              },
            }}
          />
          {mapObjectValues(
            parsedFormErrors?.dateOfBirth,
            (key: any, index: any) => (
              <ErrorHelperText
                key={`create-new-driver-driver-profile-details-date-of-birth-errors-${index}`}
                errorText={parsedFormErrors?.dateOfBirth[key]}
              />
            )
          )}
        </Grid>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={12}>
          <TextField
            label={"Address"}
            fullWidth
            value={address}
            error={Boolean(isAnyKeyValueTrue(formErrors.address))}
            onChange={(e) => {
              setAddress(e.target.value);
            }}
            onFocus={() => {
              setFormFieldToFocused("address");
            }}
            onBlur={() => {
              setFormFieldToBlurred("address");
            }}
          />
          {mapObjectValues(
            parsedFormErrors?.address,
            (key: any, index: any) => (
              <ErrorHelperText
                key={`create-new-driver-driver-profile-details-address-errors-${index}`}
                errorText={parsedFormErrors?.address[key]}
              />
            )
          )}
        </Grid>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={12}>
          <TextField
            label={"City"}
            fullWidth
            value={city}
            error={Boolean(isAnyKeyValueTrue(formErrors.city))}
            onChange={(e) => {
              setCity(e.target.value);
            }}
            onFocus={() => {
              setFormFieldToFocused("city");
            }}
            onBlur={() => {
              setFormFieldToBlurred("city");
            }}
          />
          {mapObjectValues(parsedFormErrors?.city, (key: any, index: any) => (
            <ErrorHelperText
              key={`create-new-driver-driver-profile-city-errors-${index}`}
              errorText={parsedFormErrors?.city[key]}
            />
          ))}
        </Grid>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Province</InputLabel>
            <Select
              onChange={(e) => {
                setProvince(e.target.value);
              }}
              error={Boolean(isAnyKeyValueTrue(formErrors.province))}
              onFocus={() => {
                setFormFieldToFocused("province");
              }}
              onBlur={() => {
                setFormFieldToBlurred("province");
              }}
              value={province}
              label="Province"
            >
              {provinces.map((province) => (
                <MenuItem
                  key={`driver-profile-details-province-options-${province.name}`}
                  value={province.name}
                >
                  {province.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {mapObjectValues(
            parsedFormErrors?.province,
            (key: any, index: any) => (
              <ErrorHelperText
                key={`create-new-driver-driver-profile-province-errors-${index}`}
                errorText={parsedFormErrors?.province[key]}
              />
            )
          )}
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={"Postal Code"}
            fullWidth
            value={driverPostalCode}
            error={Boolean(isAnyKeyValueTrue(formErrors.driverPostalCode))}
            onFocus={() => {
              setFormFieldToFocused("driverPostalCode");
            }}
            onBlur={() => {
              setFormFieldToBlurred("driverPostalCode");
            }}
            onChange={(e) => {
              setDriverPostalCode(e.target.value);
            }}
          />
          {mapObjectValues(
            parsedFormErrors?.driverPostalCode,
            (key: any, index: any) => (
              <ErrorHelperText
                key={`create-new-driver-driver-profile-driver-postal-code-errors-${index}`}
                errorText={parsedFormErrors?.driverPostalCode[key]}
              />
            )
          )}
        </Grid>
      </Grid>
      {/* <Grid container item spacing={2}>
        <Grid item xs={12}>
          <TextField
            label={"License Number"}
            fullWidth
            value={drivingLicenseNumber}
            error={Boolean(isAnyKeyValueTrue(formErrors.drivingLicenseNumber))}
            onChange={(e) => {
              setDrivingLicenseNumber(e.target.value);
            }}
            onFocus={() => {
              setFormFieldToFocused("drivingLicenseNumber");
            }}
            onBlur={() => {
              setFormFieldToBlurred("drivingLicenseNumber");
            }}
          />
          {mapObjectValues(
            parsedFormErrors?.drivingLicenseNumber,
            (key: any, index: any) => (
              <ErrorHelperText
                key={`create-new-driver-driver-profile-driving-license-number-errors-${index}`}
                errorText={parsedFormErrors?.drivingLicenseNumber[key]}
              />
            )
          )}
        </Grid>
      </Grid> */}
      <Grid container item spacing={2} pb={4}>
        <Grid item>
          <Typography>Avatar (Required)</Typography>
        </Grid>
        <ImageUploadSection
          uploadedFile={avatar}
          loading={avatarUploadLoading}
          label={"Avatar"}
          handleUpload={handleAvatarUpload}
        />
      </Grid>
    </>
  );
};

export default DriverProfileDetailsForm;
